import React, { useState } from 'react';

import { Checkbox } from '@swyftx/aviary/atoms/Checkbox';
import { Input } from '@swyftx/aviary/atoms/Input';
import { Label } from '@swyftx/aviary/atoms/Label';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body } from '@swyftx/aviary/atoms/Typography';

import { Big } from '@shared/safe-big';

import { Controller, useForm } from 'react-hook-form';

import { LoansStepContainer } from './LoansStepContainer';
import { useLoanApplicationSelector, useLoanApplicationService } from '../../xstate/LoanApplication.context';

interface FinancialsFormValues {
  monthlyIncome?: Big;
  expectIncomeDecrease?: boolean;
  expectedMonthlyIncome?: Big;
  monthlyExpenses?: Big;
  expectMonthlyExpensesDecrease?: boolean;
  expectedMonthlyExpenses?: Big;
  totalExistingCreditLimits?: Big;
  expectCreditLimitIncrease?: boolean;
  expectedTotalExistingCreditLimits?: Big;
}

const TellUsAboutYourFinancialsStep: React.FC = () => {
  const loanApplicationService = useLoanApplicationService();
  const applicationData = useLoanApplicationSelector((state) => state.context.applicationData);

  const [expectIncomeDecrease, setExpectIncomeDecrease] = useState(!!applicationData.expectedMonthlyIncome);
  const [expectMonthlyExpensesDecrease, setExpectMonthlyExpensesDecrease] = useState(
    !!applicationData.expectedMonthlyExpenses,
  );
  const [expectCreditLimitIncrease, setExpectCreditLimitIncrease] = useState(
    !!applicationData.expectedTotalExistingCreditLimits,
  );

  const {
    control,
    handleSubmit,
    formState: { isValid },
    setValue,
    trigger,
  } = useForm<FinancialsFormValues>({
    mode: 'onChange',
    defaultValues: {
      monthlyIncome: applicationData.monthlyIncome || undefined,
      expectIncomeDecrease: !!applicationData.expectedMonthlyIncome,
      expectedMonthlyIncome: applicationData.expectedMonthlyIncome || undefined,

      monthlyExpenses: applicationData.monthlyExpenses || undefined,
      expectMonthlyExpensesDecrease: !!applicationData.expectedMonthlyExpenses,
      expectedMonthlyExpenses: applicationData.expectedMonthlyExpenses || undefined,

      totalExistingCreditLimits: applicationData.totalExistingCreditLimits || undefined,
      expectCreditLimitIncrease: !!applicationData.expectedTotalExistingCreditLimits,
      expectedTotalExistingCreditLimits: applicationData.expectedTotalExistingCreditLimits || undefined,
    },
  });

  const onSubmit = (data: FinancialsFormValues) => {
    loanApplicationService.send({ type: 'NEXT', data });
  };

  return (
    <LoansStepContainer
      customTitle='Tell us about your financials'
      onSubmit={handleSubmit(onSubmit)}
      submitDisabled={!isValid}
    >
      <main className='grid gap-16'>
        <Body>To help determine your suitability, we must ask about your current financial situation.</Body>
        <div className='grid gap-32'>
          <div className='flex flex-col gap-8'>
            <Body size='small' weight='emphasis'>
              Your monthly income
            </Body>
            <Controller
              name='monthlyIncome'
              control={control}
              rules={{ required: 'Monthly income is required' }}
              render={({ field, fieldState: { error } }) => (
                <Input
                  appearance='outlined'
                  {...field}
                  id='monthly-income'
                  placeholder='Enter your monthly income'
                  title='Monthly income'
                  type='number'
                  error={!!error}
                  onChange={(e) => field.onChange(Big(e.target.value || 0))}
                  value={field.value?.toString() || ''}
                />
              )}
            />
            <FlexLayout direction='row' alignItems='center' spacing={8}>
              <Controller
                name='expectIncomeDecrease'
                control={control}
                render={({ field }) => (
                  <Checkbox
                    className='h-20 w-20 border-2 border-color-border-main'
                    id='monthly-income-checkbox'
                    checked={field.value || false}
                    onCheckedChange={(checked) => {
                      const boolValue = !!checked;
                      field.onChange(boolValue);
                      setExpectIncomeDecrease(boolValue);
                      if (!boolValue) {
                        setValue('expectedMonthlyIncome', undefined);
                      }
                      trigger('expectedMonthlyIncome'); // Re-validate conditional field
                    }}
                  />
                )}
              />
              <Label htmlFor='monthly-income-checkbox'>
                I expect my monthly income to <span className='font-bold'>decrease</span> over the term of the loan
              </Label>
            </FlexLayout>
          </div>

          {expectIncomeDecrease && (
            <div className='flex flex-col gap-8'>
              <Body size='small' weight='emphasis'>
                Expected monthly income
              </Body>
              <Controller
                name='expectedMonthlyIncome'
                control={control}
                rules={{ required: 'Expected decrease amount is required' }}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    appearance='outlined'
                    {...field}
                    id='expected-monthly-income-decrease'
                    placeholder='Enter expected decrease amount'
                    title='Expected Monthly Income Decrease'
                    type='number'
                    error={!!error}
                    onChange={(e) => field.onChange(Big(e.target.value || 0))}
                    value={field.value?.toString() || ''}
                  />
                )}
              />
            </div>
          )}

          <div className='flex flex-col gap-8'>
            <div className='flex flex-col gap-4'>
              <Label>Your monthly expenses</Label>
              <Body size='small'>
                This is your best estimate of your share of household expenses. Estimated avg. expenses per person in
                Australia is approximately <span className='font-semibold text-color-text-accent'>$4,441</span>.
              </Body>
            </div>
            <Controller
              name='monthlyExpenses'
              control={control}
              rules={{ required: 'Monthly expenses are required' }}
              render={({ field, fieldState: { error } }) => (
                <Input
                  appearance='outlined'
                  {...field}
                  id='monthly-expenses'
                  placeholder='Enter your monthly expenses'
                  title='Monthly expenses'
                  type='number'
                  error={!!error}
                  onChange={(e) => field.onChange(Big(e.target.value || 0))}
                  value={field.value?.toString() || ''}
                />
              )}
            />
            <FlexLayout direction='row' alignItems='center' spacing={8}>
              <Controller
                name='expectMonthlyExpensesDecrease'
                control={control}
                render={({ field }) => (
                  <Checkbox
                    className='h-20 w-20 border-2 border-color-border-main'
                    id='monthly-expenses-checkbox'
                    checked={field.value || false}
                    onCheckedChange={(checked) => {
                      const boolValue = !!checked;
                      field.onChange(boolValue);
                      setExpectMonthlyExpensesDecrease(boolValue);
                      if (!boolValue) {
                        setValue('expectMonthlyExpensesDecrease', undefined);
                      }
                      trigger('expectMonthlyExpensesDecrease');
                    }}
                  />
                )}
              />
              <Label htmlFor='monthly-expenses-checkbox'>
                I expect my monthly expenses to <span className='font-bold'>decrease</span> over the term of the loan.
              </Label>
            </FlexLayout>
          </div>
          {expectMonthlyExpensesDecrease && (
            <div className='flex flex-col gap-8'>
              <Body size='small' weight='emphasis'>
                Expected monthly expenses
              </Body>
              <Controller
                name='expectedMonthlyExpenses'
                control={control}
                rules={{ required: 'Expected decrease amount is required' }}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    appearance='outlined'
                    {...field}
                    id='expected-monthly-expenses-decrease'
                    placeholder='Enter expected decrease amount'
                    title='Expected Monthly Expenses Decrease'
                    type='number'
                    error={!!error}
                    onChange={(e) => field.onChange(Big(e.target.value || 0))}
                    value={field.value?.toString() || ''}
                  />
                )}
              />
            </div>
          )}

          <div className='flex flex-col gap-8'>
            <div className='flex flex-col gap-4'>
              <Label>Total existing credit limits</Label>
              <Body size='small'>
                This includes all and any credit limits such as mortgages, personal loans or credit cards.
              </Body>
            </div>
            <Controller
              name='totalExistingCreditLimits'
              control={control}
              rules={{ required: 'Total existing credit limits are required' }}
              render={({ field, fieldState: { error } }) => (
                <Input
                  appearance='outlined'
                  {...field}
                  id='existing-credit-limits'
                  placeholder='Enter total credit limits'
                  error={!!error}
                  onChange={(e) => field.onChange(Big(e.target.value || 0))}
                  value={field.value?.toString() || ''}
                />
              )}
            />
            <FlexLayout direction='row' alignItems='center' spacing={8}>
              <Controller
                name='expectCreditLimitIncrease'
                control={control}
                render={({ field }) => (
                  <Checkbox
                    className='h-20 w-20 border-2 border-color-border-main'
                    id='existing-credit-limits-checkbox'
                    checked={field.value || false}
                    onCheckedChange={(checked) => {
                      const boolValue = !!checked;
                      field.onChange(boolValue);
                      setExpectCreditLimitIncrease(boolValue);
                      if (!boolValue) {
                        setValue('expectedTotalExistingCreditLimits', undefined);
                      }
                      trigger('expectedTotalExistingCreditLimits');
                    }}
                  />
                )}
              />
              <Label htmlFor='existing-credit-limits-checkbox'>
                I expect my credit limits to <span className='font-bold'>increase</span> over the term of the loan.
              </Label>
            </FlexLayout>
          </div>

          {expectCreditLimitIncrease && (
            <div className='flex flex-col gap-8'>
              <Body size='small' weight='emphasis'>
                Expected credit limits
              </Body>
              <Controller
                name='expectedTotalExistingCreditLimits'
                control={control}
                rules={{ required: 'Expected increase amount is required' }}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    appearance='outlined'
                    {...field}
                    id='expected-credit-limit-increase'
                    placeholder='Enter expected increase amount'
                    title='Expected Credit Limit Increase'
                    type='number'
                    error={!!error}
                    onChange={(e) => field.onChange(Big(e.target.value || 0))}
                    value={field.value?.toString() || ''}
                  />
                )}
              />
            </div>
          )}
        </div>
      </main>
    </LoansStepContainer>
  );
};

export { TellUsAboutYourFinancialsStep };
